// Shared pieces of the accounting and revenue share screens
// (docs/plans/phase3-accounting-and-revenue-share.md §6).
import { useMutation } from '@tanstack/react-query';
import { Link, useLocation } from '@tanstack/react-router';
import {
  addDays,
  eatDayOf,
  formatIsoDay,
  type JournalExportFormat,
  REVENUE_SHARE_BASIS_WORDS,
  SHARE_EVENT_WORDS,
} from '@wodi/domain';
import type {
  JournalView,
  RevenueShareStatementStatus,
  RevenueShareStatementView,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Notice,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  type TagState,
} from '@wodi/ui';

import { downloadJournal } from '../api/accounting.ts';
import { money, moneyColumn } from '../billing/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen, TableScroll } from '../layout/screen.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './accounting.module.css';

export { REVENUE_SHARE_BASIS_WORDS };

/** Today and yesterday in East Africa Time, as `YYYY-MM-DD`. */
export function todayIso(): string {
  return formatIsoDay(eatDayOf(new Date()));
}

export function dayOffsetIso(days: number): string {
  return formatIsoDay(addDays(eatDayOf(new Date()), days));
}

function Tabs({
  label,
  links,
}: {
  label: string;
  links: readonly { to: string; label: string }[];
}) {
  const location = useLocation();
  return (
    <nav aria-label={label} className={screen.tabs}>
      {links.map((l) => (
        <Link
          key={l.to}
          to={l.to}
          activeOptions={{ exact: true }}
          {...(location.pathname === l.to ? { 'aria-current': 'page' as const } : {})}
        >
          {l.label}
        </Link>
      ))}
    </nav>
  );
}

export function AccountingTabs() {
  return (
    <Tabs
      label="Accounting"
      links={[
        { to: '/accounting', label: 'Journal' },
        { to: '/accounting/periods', label: 'Periods' },
        { to: '/accounting/accounts', label: 'Accounts' },
      ]}
    />
  );
}

export function RevenueShareTabs() {
  return (
    <Tabs
      label="Revenue share"
      links={[
        { to: '/revenue-share', label: 'Statements' },
        { to: '/revenue-share/rules', label: 'Rules' },
        { to: '/revenue-share/settings', label: 'Settings' },
      ]}
    />
  );
}

/** Shown in place of a page the reader may not open. */
export function NotPermitted({ what }: { what: string }) {
  return (
    <Notice tone="info">
      {`You don't have permission to see ${what}. Ask an administrator if you need it.`}
    </Notice>
  );
}

export function OfflineList() {
  const online = useOnline();
  if (online) return null;
  return (
    <Notice tone="info">
      You&apos;re offline. This shows what was last loaded; it updates when you&apos;re back online.
    </Notice>
  );
}

const FORMAT_WORDS: Record<JournalExportFormat, { button: string; toast: string }> = {
  csv: { button: 'Download journal', toast: 'Journal downloaded' },
  detail: { button: 'Download detail', toast: 'Journal detail downloaded' },
  quickbooks: { button: 'Download for QuickBooks', toast: 'QuickBooks file downloaded' },
};

/** The three journal files; each download is audited on the server first. */
export function JournalDownloads({
  target,
}: {
  target: { from: string; to: string } | { periodId: string };
}) {
  const online = useOnline();
  const download = useMutation({
    mutationFn: (format: JournalExportFormat) => downloadJournal(target, format),
    onSuccess: (name, format) => {
      toasts.show({ title: FORMAT_WORDS[format].toast, description: name });
    },
  });
  return (
    <div className={styles.form}>
      <div className={styles.actions}>
        {(['csv', 'detail', 'quickbooks'] as const).map((format) => (
          <Button
            key={format}
            variant={format === 'quickbooks' ? 'primary' : 'secondary'}
            isDisabled={!online}
            isPending={download.isPending && download.variables === format}
            pendingLabel="Downloading…"
            onPress={() => {
              download.mutate(format);
            }}
          >
            {FORMAT_WORDS[format].button}
          </Button>
        ))}
      </div>
      {download.isError ? (
        <Notice tone="error">
          {errorText(download.error, "The file wasn't downloaded. Try again.")}
        </Notice>
      ) : null}
      {online ? null : <Notice tone="info">Downloading needs a connection to Wodi.</Notice>}
    </div>
  );
}

/** Totals, the check against Wodi's reports, the trial balance and the entries. */
export function JournalDetail({ journal }: { journal: JournalView }) {
  const t = journal.totals;
  return (
    <>
      <Section
        title="Totals"
        actions={
          <Tag state={t.balanced ? 'done' : 'failed'}>
            {t.balanced ? 'Balanced' : 'Does not balance'}
          </Tag>
        }
      >
        <dl className={styles.facts}>
          <dt>Days</dt>
          <dd>
            {journal.from === journal.to
              ? formatIsoDate(journal.from)
              : `${formatIsoDate(journal.from)} to ${formatIsoDate(journal.to)}`}
          </dd>
          <dt>Debits</dt>
          <dd>{money(t.debit)}</dd>
          <dt>Credits</dt>
          <dd>{money(t.credit)}</dd>
          <dt>Entries</dt>
          <dd>{t.entryCount}</dd>
          <dt>Late lines</dt>
          <dd>{t.lateLineCount === 0 ? 'None' : String(t.lateLineCount)}</dd>
        </dl>
        {t.lateLineCount > 0 ? (
          <Notice tone="info">
            Late lines are changes to days in a closed period. They are posted on the first day
            after it, so the closed period stays as it was.
          </Notice>
        ) : null}
        {journal.unvaluedStock.movementCount > 0 ? (
          <Notice tone="info">
            {`${String(journal.unvaluedStock.movementCount)} stock ${journal.unvaluedStock.movementCount === 1 ? 'movement' : 'movements'} of ${String(journal.unvaluedStock.itemCount)} ${journal.unvaluedStock.itemCount === 1 ? 'item' : 'items'} could not be valued: the batch was never received on a GRN. They are left out of the journal, not valued at zero.`}
          </Notice>
        ) : null}
        {journal.unpricedChargeCount > 0 ? (
          <Notice tone="info">
            {`${String(journal.unpricedChargeCount)} ${journal.unpricedChargeCount === 1 ? 'charge has' : 'charges have'} no price yet, so no revenue is posted for them. Price them in the cashier's unpriced list.`}
          </Notice>
        ) : null}
      </Section>
      {journal.status === 'open' ? (
        <Section
          title="Against Wodi's reports"
          description="Revenue, waivers, discounts and credit notes from the revenue report; money in and out from daily collections. Late lines are left out on both sides."
        >
          <TableScroll>
            <Table flat aria-label="Journal against Wodi's reports">
              <TableHeader>
                <Column isRowHeader>Figure</Column>
                <Column>Report</Column>
                <Column numeric>Journal (KES)</Column>
                <Column numeric>Report (KES)</Column>
                <Column>Check</Column>
              </TableHeader>
              <TableBody
                items={journal.reconciliation.map((r) => ({ ...r, id: r.label }))}
                renderEmptyState={() => 'Nothing was billed or paid on these days.'}
              >
                {(r) => (
                  <Row id={r.id}>
                    <Cell>{r.label}</Cell>
                    <Cell>{r.report === 'revenue' ? 'Revenue' : 'Daily collections'}</Cell>
                    <Cell numeric>{moneyColumn(r.journal)}</Cell>
                    <Cell numeric>{moneyColumn(r.reported)}</Cell>
                    <Cell>
                      <Tag state={r.matches ? 'done' : 'failed'}>
                        {r.matches ? 'Agrees' : 'Differs'}
                      </Tag>
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        </Section>
      ) : null}
      <Section title="Trial balance" description="Debits and credits per account on these days.">
        <TableScroll>
          <Table flat aria-label="Trial balance">
            <TableHeader>
              <Column isRowHeader>Account</Column>
              <Column>Code</Column>
              <Column numeric>Debit (KES)</Column>
              <Column numeric>Credit (KES)</Column>
            </TableHeader>
            <TableBody
              items={journal.trialBalance.map((r) => ({ ...r, id: `${r.key}|${r.name}` }))}
              renderEmptyState={() => 'No entries on these days.'}
            >
              {(r) => (
                <Row id={r.id}>
                  <Cell>{r.name}</Cell>
                  <Cell>{r.code === null ? '' : <span className="wodi-id">{r.code}</span>}</Cell>
                  <Cell numeric>{moneyColumn(r.debit)}</Cell>
                  <Cell numeric>{moneyColumn(r.credit)}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
      <Section
        title="Entries"
        description="One entry per day and kind of event, as the files carry them."
      >
        <TableScroll>
          <Table flat aria-label="Journal entries">
            <TableHeader>
              <Column isRowHeader>Journal number</Column>
              <Column>Date</Column>
              <Column>Description</Column>
              <Column>Account</Column>
              <Column>Payer or supplier</Column>
              <Column numeric>Debit (KES)</Column>
              <Column numeric>Credit (KES)</Column>
            </TableHeader>
            <TableBody
              items={journal.entries.flatMap((e) =>
                e.lines.map((l, i) => ({
                  id: `${e.journalNumber}|${String(i)}`,
                  entry: e,
                  line: l,
                  first: i === 0,
                })),
              )}
              renderEmptyState={() => 'No entries on these days.'}
            >
              {(r) => (
                <Row id={r.id}>
                  <Cell>
                    <span className="wodi-id">{r.entry.journalNumber}</span>
                  </Cell>
                  <Cell>{r.first ? formatIsoDate(r.entry.postedOn) : ''}</Cell>
                  <Cell>{r.first ? r.entry.description : ''}</Cell>
                  <Cell>{r.line.name}</Cell>
                  <Cell>{r.line.counterparty ?? ''}</Cell>
                  <Cell numeric>{r.line.debit.cents === '0' ? '' : moneyColumn(r.line.debit)}</Cell>
                  <Cell numeric>
                    {r.line.credit.cents === '0' ? '' : moneyColumn(r.line.credit)}
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
    </>
  );
}

// --- Revenue share ------------------------------------------------------------------------------

export const STATEMENT_STATUS_WORDS: Record<RevenueShareStatementStatus, string> = {
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  paid: 'Paid',
};

const STATEMENT_STATUS_TAGS: Record<RevenueShareStatementStatus, TagState> = {
  awaiting_approval: 'pending',
  approved: 'active',
  rejected: 'neutral',
  withdrawn: 'neutral',
  paid: 'done',
};

export function StatementTag({ status }: { status: RevenueShareStatementStatus }) {
  return <Tag state={STATEMENT_STATUS_TAGS[status]}>{STATEMENT_STATUS_WORDS[status]}</Tag>;
}

export function periodText(from: string, to: string): string {
  return from === to ? formatIsoDate(from) : `${formatIsoDate(from)} to ${formatIsoDate(to)}`;
}

/** A statement's figures, lines and payouts, the same for the office and the practitioner. */
export function StatementBody({ statement }: { statement: RevenueShareStatementView }) {
  const s = statement;
  const negative = BigInt(s.payable.cents) < 0n;
  return (
    <>
      <Section title={s.practitioner.displayName} actions={<StatementTag status={s.status} />}>
        <dl className={styles.facts}>
          <dt>Statement</dt>
          <dd>
            <span className="wodi-id">{s.number}</span>
          </dd>
          <dt>Period</dt>
          <dd>{periodText(s.periodFrom, s.periodTo)}</dd>
          <dt>Earned</dt>
          <dd>{money(s.earned)}</dd>
          <dt>Adjustments</dt>
          <dd>{money(s.adjustments)}</dd>
          <dt>Payable</dt>
          <dd>{money(s.payable)}</dd>
          <dt>Paid</dt>
          <dd>{money(s.paid)}</dd>
          <dt>Prepared</dt>
          <dd>{`${s.preparedBy.displayName}, ${formatInstant(s.preparedAt)}`}</dd>
          {s.decidedBy === null || s.decidedAt === null ? null : (
            <>
              <dt>{s.status === 'rejected' ? 'Rejected' : 'Approved'}</dt>
              <dd>{`${s.decidedBy.displayName}, ${formatInstant(s.decidedAt)}`}</dd>
            </>
          )}
          {s.decisionNote === null ? null : (
            <>
              <dt>Note</dt>
              <dd>{s.decisionNote}</dd>
            </>
          )}
        </dl>
        {negative ? (
          <Notice tone="info">
            Reversals are more than the share earned, so this statement is owed back. It is settled
            outside Wodi; no payout is recorded.
          </Notice>
        ) : null}
      </Section>
      <Section
        title="Lines"
        description="Each service, with the part of it that earned or reversed a share. No patient details are shown."
      >
        <TableScroll>
          <Table flat aria-label="Statement lines">
            <TableHeader>
              <Column isRowHeader>Service</Column>
              <Column>Service date</Column>
              <Column>Event</Column>
              <Column>Event date</Column>
              <Column>Share</Column>
              <Column numeric>Charge (KES)</Column>
              <Column numeric>Base (KES)</Column>
              <Column numeric>Share (KES)</Column>
            </TableHeader>
            <TableBody items={s.lines.map((l) => ({ ...l, id: String(l.lineNo) }))}>
              {(l) => (
                <Row id={l.id}>
                  <Cell>
                    {l.itemName} <span className="wodi-id">{l.itemCode}</span>
                  </Cell>
                  <Cell>{formatIsoDate(l.serviceDay)}</Cell>
                  <Cell>{SHARE_EVENT_WORDS[l.kind]}</Cell>
                  <Cell>{formatIsoDate(l.eventOn)}</Cell>
                  <Cell>{l.rule}</Cell>
                  <Cell numeric>{moneyColumn(l.chargeTotal)}</Cell>
                  <Cell numeric>{moneyColumn(l.base)}</Cell>
                  <Cell numeric>{moneyColumn(l.share)}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
      <Section title="Payouts" description="Paid outside Wodi and recorded here.">
        <TableScroll>
          <Table flat aria-label="Payouts">
            <TableHeader>
              <Column isRowHeader>Paid on</Column>
              <Column>Method</Column>
              <Column>Reference</Column>
              <Column>Recorded by</Column>
              <Column numeric>Amount (KES)</Column>
            </TableHeader>
            <TableBody items={s.payouts} renderEmptyState={() => 'No payouts recorded yet.'}>
              {(p) => (
                <Row id={p.id}>
                  <Cell>{formatIsoDate(p.paidOn)}</Cell>
                  <Cell>{PAYOUT_WORDS[p.method]}</Cell>
                  <Cell>
                    {p.reference === null ? '' : <span className="wodi-id">{p.reference}</span>}
                  </Cell>
                  <Cell>{p.recordedBy.displayName}</Cell>
                  <Cell numeric>{moneyColumn(p.amount)}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
    </>
  );
}

export const PAYOUT_WORDS = {
  cash: 'Cash',
  mpesa: 'M-Pesa',
  bank_transfer: 'Bank transfer',
  cheque: 'Cheque',
} as const;

export function basisWords(basis: 'billed' | 'collected'): string {
  return REVENUE_SHARE_BASIS_WORDS[basis];
}
